import { useState } from "react";
import { toast } from "sonner";
import { MonitorSmartphone, Copy, ExternalLink } from "lucide-react";
import { api, formatErr, fmtTime, useFetch } from "@/lib/api";
import { Pill } from "@/components/common";

const VIA = { master_dashboard: "Master dashboard", portal: "Customer portal", rustdesk_direct: "Direct RustDesk (not via CodeRed)" };
const dur = (x) => {
  if (x.status === "active") return <Pill tone="healthy">In progress</Pill>;
  if (x.status === "pending") return <Pill tone="waiting">Waiting to connect</Pill>;
  if (x.status === "not_connected") return "Not connected";
  if (!x.status) return "—";
  const m = Math.round((x.duration_s || 0) / 6) / 10;
  return `${m} min${x.status === "timed_out" ? " (timed out)" : ""}`;
};

export const openRemote = async (path, setInfo) => {
  try {
    const r = await api.post(path);
    setInfo(r.data);
    window.location.href = r.data.uri;
    toast.success("Opening RustDesk… session logged and the user is notified");
  } catch (e) { toast.error(formatErr(e)); }
};

export function RemoteCreds({ info, onClose }) {
  if (!info) return null;
  const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success("Copied"));
  return (
    <div className="mt-3 border border-gray-200 bg-gray-50 p-3 text-sm" data-testid="remote-creds">
      <div className="flex flex-wrap gap-4 items-center">
        <span>ID <b className="font-mono" data-testid="remote-id">{info.id}</b> <button onClick={() => copy(info.id)} className="text-gray-500 hover:text-gray-900" data-testid="remote-copy-id"><Copy className="w-3.5 h-3.5 inline" /></button></span>
        <span>Password <b className="font-mono" data-testid="remote-password">{info.password}</b> <button onClick={() => copy(info.password)} className="text-gray-500 hover:text-gray-900" data-testid="remote-copy-pw"><Copy className="w-3.5 h-3.5 inline" /></button></span>
        <a href={info.uri} className="inline-flex items-center gap-1 underline" data-testid="remote-open-again"><ExternalLink className="w-3.5 h-3.5" />Open again</a>
        <button onClick={onClose} className="text-xs text-gray-500 underline" data-testid="remote-hide">Hide</button>
      </div>
      <p className="text-xs text-gray-500 mt-2">If RustDesk didn't open, install it on your computer from rustdesk.com, then enter the ID and password.</p>
    </div>
  );
}

export function RemoteAccessCard({ device, canManage, onDone }) {
  const [info, setInfo] = useState(null);
  const { data: sessions, reload: reloadSessions } = useFetch(`/devices/${device.id}/remote/sessions`, [device.id]);
  const r = device.remote;
  const blocked = device.ops && device.ops.remote_access_allowed === false;
  const setup = async () => {
    try { await api.post(`/devices/${device.id}/remote/setup`); toast.success("RustDesk install queued. The ID appears after the next check-in"); onDone(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="card-flat p-4 mb-6" data-testid="remote-access-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><MonitorSmartphone className="w-5 h-5 text-red-600" />
          <div><div className="font-semibold text-sm">Unattended remote access</div>
            <div className="text-xs text-gray-500" data-testid="remote-status">{r?.id ? `RustDesk ID ${r.id} · installed ${fmtTime(r.installed_at)}` : "Not set up. Installs RustDesk silently with a random password"}</div></div></div>
        {blocked && <span className="text-xs text-gray-500" data-testid="remote-blocked-note">Remote access is turned off in Settings → Maintenance & monitoring</span>}
        {canManage && !blocked && device.status === "active" && (r?.id
          ? <button onClick={() => openRemote(`/devices/${device.id}/remote/connect`, setInfo).then(reloadSessions)} className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors" data-testid="remote-connect-btn">Connect</button>
          : <button onClick={setup} className="px-4 py-2 border border-gray-900 text-sm hover:bg-gray-900 hover:text-white transition-colors" data-testid="remote-setup-btn">Enable remote access</button>)}
      </div>
      <RemoteCreds info={info} onClose={() => setInfo(null)} />
      {r?.id && <p className="text-xs text-gray-400 mt-2">Every session is audit-logged and a "CodeRed support connected" notice is shown on the PC.</p>}
      {sessions?.length > 0 && (
        <div className="mt-4"><div className="text-xs font-semibold text-gray-500 uppercase mb-1">Remote sessions</div>
          <div className="overflow-x-auto"><table className="data-table" data-testid="remote-sessions-table"><thead><tr><th>Started</th><th>Ended</th><th>Duration</th><th>Technician</th><th>From</th></tr></thead>
            <tbody>{sessions.map((x) => <tr key={x.id || x.at} data-testid={`remote-session-${x.id}`}><td className="text-xs">{fmtTime(x.connected_at || x.at)}</td><td className="text-xs" data-testid={`remote-session-end-${x.id}`}>{x.ended_at ? fmtTime(x.ended_at) : "—"}</td><td className="text-xs" data-testid={`remote-session-duration-${x.id}`}>{dur(x)}</td><td className="text-xs">{x.by}</td><td className="text-xs">{VIA[x.via] || "Customer portal"}</td></tr>)}</tbody></table></div></div>
      )}
    </div>
  );
}
